import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
  OmakasePolymorphicRootComponent,
} from 'omakase/utils/OmakasePolymorphicComponent';
import { Theme } from '@emotion/react';
import { padButtonGroup } from 'omakase/PadButton/padButtonCssDefs';
import React from 'react';

export type PadButtonNumColumns = 1 | 2 | 3;

export interface PadButtonGroupProps {
  /**
   * Whether or not the group has a background color
   */
  background: boolean;
  /**
   * The number of grid columns to render
   */
  numColumns: PadButtonNumColumns;
  /**
   * Gap between columns, in terms of theme.spacing units
   */
  columnGap?: number;
}

const PadButtonGroupDefaultElement = 'div';
type PadButtonGroupTypeGuard = 'div';

const PadButtonGroup: OmakasePolymorphicComponent<
  PadButtonGroupProps,
  typeof PadButtonGroupDefaultElement,
  PadButtonGroupTypeGuard
> = React.forwardRef(
  <E extends React.ElementType = typeof PadButtonGroupDefaultElement>(
    {
      component,
      columnGap = 2.5,
      background,
      children,
      numColumns,
      ...rest
    }: OmakasePolymorphicComponentProps<PadButtonGroupProps, E, PadButtonGroupTypeGuard>,
    ref: React.Ref<HTMLDivElement> | undefined
  ) => {
    const Component: React.ElementType = component || PadButtonGroupDefaultElement;
    return (
      <OmakasePolymorphicRootComponent
        ref={ref}
        component={Component}
        css={(theme: Theme) => padButtonGroup(theme, numColumns, { background, columnGap })}
        role="group"
        {...rest}
      >
        {children}
      </OmakasePolymorphicRootComponent>
    );
  }
);

export default PadButtonGroup;
